/**
 * StepCoachingContent — coaching + real records for a 10-Step tile,
 * rendered inside a FloatingWindow (non-blocking).
 *
 *   • Real records only — the backend no longer returns "Sample 1/2/3"
 *     placeholders. An empty step shows a proper empty state with a
 *     `+ New Brief` CTA.
 *   • Per-row inline edit (pencil): rename business/full-name, change
 *     status, edit notes. Rename propagates to CRM, timeline, invoices.
 *   • Per-row archive button: soft-delete, hidden from active list.
 *   • Active | Archived tabs at the top switch between the two views.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Plus, Pencil, Archive, ArchiveRestore, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, apiClient } from "@/lib/auth";
import { useNexOs } from "@/lib/nexOs";
import { useNdsEditor } from "@/lib/ndsEditor";

const inputCls = "bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none px-2 py-1.5 text-nex-silver font-mono text-xs transition-all duration-200 w-full";

export default function StepCoachingContent({ win }) {
  const { mode } = useNexOs();
  const { fireTileAction } = useNdsEditor();
  const { step, verticalId } = win.payload || {};
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("active");   // "active" | "archived"
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy]       = useState(null);       // record id currently being written

  const fetchRecords = async () => {
    if (!step || !verticalId) return;
    setLoading(true);
    try {
      const url = `${API}/nex-os/step-records`;
      const { data: d } = await axios.get(url, {
        params: { vertical: verticalId, step: step.id, mode, archived: tab === "archived" },
      });
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRecords(); }, [verticalId, step?.id, mode, tab]);

  if (!step) return null;

  const patch = async (rec, body) => {
    setBusy(rec.id);
    try {
      const { data } = await apiClient.put(`/admin/nex-os/records/${rec.id}`, body);
      const sync = data?.propagation?.synced?.length;
      toast.success(sync ? `Saved · synced ${sync} surface${sync > 1 ? "s" : ""}` : "Saved");
      setEditingId(null);
      await fetchRecords();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const archive = async (rec) => {
    if (!window.confirm(`Archive "${rec.label}"? It will move to the Archived tab.`)) return;
    setBusy(rec.id);
    try {
      await apiClient.delete(`/admin/nex-os/records/${rec.id}`);
      toast.success("Archived");
      await fetchRecords();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
    finally { setBusy(null); }
  };

  const unarchive = async (rec) => {
    setBusy(rec.id);
    try {
      await apiClient.post(`/admin/nex-os/records/${rec.id}/unarchive`);
      toast.success("Restored");
      await fetchRecords();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="flex flex-col text-nex-silver">
      {/* Coaching header */}
      <div className="p-5 border-b border-white/10">
        <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-muted uppercase">
          Step {step.id} · Coaching · {mode.toUpperCase()}
        </div>
        <div className="font-serif text-lg font-bold text-nex-silver mt-1">{step.title}</div>
        <p className="text-nex-silver/85 leading-relaxed text-sm mt-3">
          {data?.coaching || step.coaching || "—"}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="nds-pill nds-pill--gold" style={{ padding: "5px 12px", fontSize: "0.6rem" }}>
            Score {data?.score || step.score || 10} / 10
          </span>
          {step.id === "01" && (
            <button
              type="button"
              onClick={() => fireTileAction({ kind: "brief_intake", title: "New Friction Audit Brief" })}
              className="nds-pill nds-pill--live inline-flex items-center gap-1.5"
              style={{ padding: "5px 12px", fontSize: "0.6rem" }}
              data-testid="nds-step-cta"
            >
              <Plus className="w-3 h-3" /> New Brief
            </button>
          )}
        </div>
      </div>

      {/* Active | Archived tabs */}
      <div className="px-5 pt-4 pb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5" role="tablist">
          {[
            { k: "active",   label: "Active" },
            { k: "archived", label: "Archived" },
          ].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              data-testid={`nds-step-records-tab-${k}`}
              className={`px-2.5 py-1 border font-mono text-[0.55rem] tracking-[0.22em] uppercase transition-all ${
                tab === k
                  ? "border-nex-gold/60 bg-nex-gold/10 text-nex-gold"
                  : "border-white/10 text-nex-chrome hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {data?.records?.length > 0 && (
          <span className="font-mono text-[0.55rem] tracking-widest text-nex-muted uppercase">
            {data.records.length} shown
          </span>
        )}
      </div>

      {/* Records */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="px-5 py-6 font-mono text-xs text-nex-muted" data-testid="nds-step-records-loading">
            Loading records…
          </div>
        ) : (data?.records || []).length === 0 ? (
          <EmptyState step={step} tab={tab} onNewBrief={() => fireTileAction({ kind: "brief_intake" })} />
        ) : (
          <div className="pb-3">
            {(data?.records || []).map((r) => (
              <RecordRow
                key={r.id}
                r={r}
                mode={mode}
                tab={tab}
                busy={busy === r.id}
                isEditing={editingId === r.id}
                onEdit={() => setEditingId(r.id)}
                onCancel={() => setEditingId(null)}
                onSave={(body) => patch(r, body)}
                onArchive={() => archive(r)}
                onUnarchive={() => unarchive(r)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Row ============ */
function RecordRow({ r, mode, tab, busy, isEditing, onEdit, onCancel, onSave, onArchive, onUnarchive }) {
  const linkable = mode === "live" && r.brief_id && tab === "active" && !isEditing;
  const Tag = linkable ? "a" : "div";
  const props = linkable
    ? { href: `/admin/brief/${r.brief_id}`, target: "_blank", rel: "noopener noreferrer" }
    : {};
  if (isEditing) return <EditForm r={r} busy={busy} onCancel={onCancel} onSave={onSave} />;
  return (
    <Tag
      {...props}
      className={`w-full flex items-center justify-between gap-3 px-5 py-2.5 border-b border-white/5 hover:bg-white/[0.03] no-underline text-nex-silver ${linkable ? "cursor-pointer" : "cursor-default"}`}
      data-testid={`nds-step-record-${r.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="font-medium text-nex-silver truncate text-sm flex items-center gap-2">
          {r.label}
          {tab === "archived" && (
            <span className="font-mono text-[0.5rem] tracking-[0.16em] uppercase px-1.5 py-0.5 border border-white/15 text-nex-muted">Archived</span>
          )}
        </div>
        <div className="text-[0.7rem] text-nex-muted truncate">{r.sub}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {tab === "active" ? (
          <>
            <IconBtn onClick={(e) => { e.preventDefault(); onEdit(); }} disabled={busy} testId={`nds-record-edit-${r.id}`} title="Edit / rename">
              <Pencil className="w-3.5 h-3.5" />
            </IconBtn>
            <IconBtn onClick={(e) => { e.preventDefault(); onArchive(); }} disabled={busy} testId={`nds-record-archive-${r.id}`} title="Archive">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
            </IconBtn>
            {linkable && <ArrowUpRight className="w-3.5 h-3.5 text-nex-muted ml-1" />}
          </>
        ) : (
          <IconBtn onClick={(e) => { e.preventDefault(); onUnarchive(); }} disabled={busy} testId={`nds-record-unarchive-${r.id}`} title="Restore">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
          </IconBtn>
        )}
      </div>
    </Tag>
  );
}

/* ============ Inline Edit Form ============ */
function EditForm({ r, busy, onCancel, onSave }) {
  const [f, setF] = useState({
    business_name: r.business_name || r.label || "",
    full_name:     r.full_name || "",
    email:         r.email || "",
    status:        r.status || "",
    notes:         r.notes || "",
  });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = (ev) => {
    ev?.preventDefault();
    onSave({
      business_name: f.business_name.trim(),
      full_name:     f.full_name.trim(),
      email:         f.email.trim(),
      status:        f.status.trim() || undefined,
      notes:         f.notes,
    });
  };
  return (
    <form
      onSubmit={submit}
      className="px-5 py-3 border-b border-nex-gold/25 bg-nex-gold/[0.03] space-y-2"
      data-testid={`nds-record-edit-form-${r.id}`}
    >
      <div className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-gold uppercase">// Edit Record</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Business Name</span>
          <input className={inputCls} value={f.business_name} onChange={set("business_name")} autoFocus data-testid={`nds-record-field-business-${r.id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Full Name</span>
          <input className={inputCls} value={f.full_name} onChange={set("full_name")} data-testid={`nds-record-field-name-${r.id}`} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Email</span>
          <input type="email" className={inputCls} value={f.email} onChange={set("email")} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Status</span>
          <input className={inputCls} value={f.status} onChange={set("status")} placeholder="new · accepted · paid" />
        </label>
      </div>
      <label className="flex flex-col gap-0.5">
        <span className="font-mono text-[0.55rem] text-nex-muted uppercase">Notes</span>
        <textarea className={inputCls} rows={2} value={f.notes} onChange={set("notes")} />
      </label>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-3 py-1 border border-white/10 text-nex-chrome hover:text-nex-silver font-mono text-[0.6rem] tracking-[0.22em] uppercase" data-testid={`nds-record-edit-cancel-${r.id}`}>
          <X className="w-3 h-3 inline mr-1" /> Cancel
        </button>
        <button type="submit" disabled={busy} className="px-3 py-1 bg-nex-gold text-nex-navy font-mono text-[0.6rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright disabled:opacity-60" data-testid={`nds-record-edit-save-${r.id}`}>
          <Save className="w-3 h-3 inline mr-1" /> {busy ? "Saving…" : "Save & Sync"}
        </button>
      </div>
      <p className="font-mono text-[0.6rem] text-nex-muted">
        Renaming syncs to <b className="text-nex-silver">CRM · timeline · invoices</b>.
      </p>
    </form>
  );
}

/* ============ Empty state ============ */
function EmptyState({ step, tab, onNewBrief }) {
  if (tab === "archived") {
    return (
      <div className="px-5 py-8 text-center font-mono text-xs text-nex-muted" data-testid="nds-step-records-empty-archived">
        No archived records at this step.
      </div>
    );
  }
  return (
    <div className="px-5 py-8 text-center flex flex-col items-center gap-3" data-testid="nds-step-records-empty">
      <div className="font-mono text-[0.65rem] tracking-[0.22em] uppercase text-nex-muted">
        No records at Step {step.id} yet
      </div>
      {step.id === "01" && (
        <button
          type="button"
          onClick={onNewBrief}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-nex-gold text-nex-navy font-mono text-[0.65rem] tracking-[0.22em] uppercase hover:bg-nex-goldbright"
          data-testid="nds-step-records-new-brief"
        >
          <Plus className="w-3.5 h-3.5" /> New Brief
        </button>
      )}
    </div>
  );
}

/* ============ Icon Button ============ */
function IconBtn({ onClick, disabled, title, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      title={title}
      className="w-6 h-6 grid place-items-center border border-transparent hover:border-white/15 hover:bg-white/5 text-nex-chrome hover:text-nex-silver disabled:opacity-40 transition-all"
    >
      {children}
    </button>
  );
}
